/* (C -_-)G===☆ CG Collection Promo Banner */
if (!customElements.get('cg-collection-promo-banner')) {
    class CGCollectionPromoBanner extends HTMLElement {
        constructor() {
            super();
            this.onDiscountStateChanged = this.onDiscountStateChanged.bind(this);
            
            // Centralized selectors and classes
            this.selectors = {
                applyButton: '[data-apply-coupon]',
                couponItem: '.cg-promo-banner__item[data-discount-tag]',
                notApplied: '.cg-promo-banner__not-applied',
                applied: '.cg-promo-banner__applied',
                buttonText: 'span',
                spinner: '.loading__spinner'
            };
            
            this.classes = {
                hidden: 'hidden',
                applied: 'applied'
            };
        }
        
        // Getter method for all elements
        get elements() {
            return {
                applyButtons: this.querySelectorAll(this.selectors.applyButton),
                couponItems: this.querySelectorAll(this.selectors.couponItem)
            };
        }

        connectedCallback() {
            // Use event delegation for individual coupon buttons
            this.handleClick = (e) => {
                const button = e.target.closest(this.selectors.applyButton);
                if (button) {
                    e.preventDefault();
                    e.stopPropagation();
                    const couponTag = button.getAttribute('data-apply-coupon');
                    if (couponTag) {
                        this.handleApplySingleCoupon(couponTag, button);
                    }
                }
            };
            
            this.addEventListener('click', this.handleClick);

            // Listen for discount state changes
            document.addEventListener('cg-ninja-discount:applied', this.onDiscountStateChanged);
            document.addEventListener('cg-ninja-discount:removed', this.onDiscountStateChanged);
            document.addEventListener('cg-ninja-discount:synced', this.onDiscountStateChanged);

            // Load all coupons from collection (works across pagination)
            this.loadAllCouponsFromCollection();

            // Run straight away: showNextCoupon already re-schedules itself
            // until GlobalDiscountManager exists, so the old 200ms wait added
            // nothing except a wider window for the reveal to shift the page.
            this.updateCouponStates();
            this.showNextCoupon();
        }

        disconnectedCallback() {
            // Remove click listener
            if (this.handleClick) {
                this.removeEventListener('click', this.handleClick);
            }
            
            document.removeEventListener('cg-ninja-discount:applied', this.onDiscountStateChanged);
            document.removeEventListener('cg-ninja-discount:removed', this.onDiscountStateChanged);
            document.removeEventListener('cg-ninja-discount:synced', this.onDiscountStateChanged);
        }

        onDiscountStateChanged() {
            this.updateCouponStates();
            this.showNextCoupon();
        }

        // Load all coupons from collection via API (works across pagination)
        async loadAllCouponsFromCollection() {
            const sectionId = this.dataset.sectionId;
            if (!sectionId) {
                console.warn('CGCollectionPromoBanner: No section ID found');
                return;
            }

            // Get initial data from Liquid-rendered JSON
            const dataScript = document.getElementById(`cg-collection-promo-data-${sectionId}`);
            if (!dataScript) {
                console.warn('CGCollectionPromoBanner: No data script found');
                return;
            }

            let collectionData;
            try {
                collectionData = JSON.parse(dataScript.textContent);
            } catch (error) {
                console.error('CGCollectionPromoBanner: Error parsing collection promo data:', error);
                return;
            }

            const { collectionHandle, initialTags, promoText } = collectionData;
            if (!collectionHandle) {
                console.warn('CGCollectionPromoBanner: No collection handle found');
                return;
            }

            // Store initial tags in sessionStorage
            const storageKey = `cg-collection-promo-tags-${collectionHandle}`;
            const allTags = new Set(initialTags || []);

            try {
                // Get existing tags from storage
                const existingTags = sessionStorage.getItem(storageKey);
                if (existingTags) {
                    try {
                        const existingTagsArray = JSON.parse(existingTags);
                        existingTagsArray.forEach(tag => {
                            if (tag && tag.trim()) {
                                allTags.add(tag.trim());
                            }
                        });
                    } catch (e) {
                        console.warn('CGCollectionPromoBanner: Error parsing existing tags from storage:', e);
                    }
                }

                // Store initial tags if they exist (even if we don't fetch more)
                if (initialTags && initialTags.length > 0) {
                    const tagsArray = Array.from(allTags).filter(tag => tag && tag.trim());
                    sessionStorage.setItem(storageKey, JSON.stringify(tagsArray));
                }

                // Check if we need to create banner items (if DOM is empty but we have tags)
                const tagsArray = Array.from(allTags).filter(tag => tag && tag.trim());
                const hasDomItems = this.elements.couponItems.length > 0;
                
                // If we have tags in storage but not in DOM, create banner items dynamically
                if (tagsArray.length > 0 && !hasDomItems) {
                    this.createBannerItemsFromStorage(tagsArray, promoText);
                } else if (tagsArray.length > 0 && hasDomItems) {
                    // We have both DOM items and storage tags - make sure they match
                    this.updateCouponStates();
                    this.showNextCoupon();
                }

            } catch (error) {
                console.error('CGCollectionPromoBanner: Error loading coupons from collection:', error);
                // Fallback to Liquid-rendered tags
                if (initialTags && initialTags.length > 0 && this.elements.couponItems.length === 0) {
                    this.createBannerItemsFromStorage(initialTags, promoText);
                }
            }
        }

        // Create banner items from stored tags (when pagination changes and DOM items are missing)
        createBannerItemsFromStorage(tags, promoText) {
            if (!tags || tags.length === 0) {
                console.warn('CGCollectionPromoBanner: No tags provided to createBannerItemsFromStorage');
                return;
            }

            let container = this.querySelector('.cg-promo-banner__container');
            if (!container) {
                // Create container if it doesn't exist
                const pageWidth = this.querySelector('.page-width');
                if (pageWidth) {
                    container = document.createElement('div');
                    container.className = 'cg-promo-banner__container';
                    pageWidth.appendChild(container);
                } else {
                    console.error('CGCollectionPromoBanner: No page-width element found');
                    return;
                }
            }

            // Get promo text from existing item or use provided/default
            const existingItem = container.querySelector('.cg-promo-banner__item');
            const textToUse = promoText || 
                (existingItem ? existingItem.querySelector('.cg-promo-banner__text')?.textContent : null) || 
                'Save with a coupon applied.';


            // Clear existing items (if any)
            container.innerHTML = '';

            // Create banner items for each tag
            tags.forEach(tag => {
                if (!tag || !tag.trim()) return;

                const tagTrimmed = tag.trim();
                const item = document.createElement('div');
                item.className = 'cg-promo-banner__item hidden';
                item.setAttribute('data-discount-tag', tagTrimmed);

                // Escape HTML in promo text
                const escapedText = textToUse.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
                const escapedTag = tagTrimmed.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');

                item.innerHTML = `
                    <div class="cg-promo-banner__content">
                        <div class="cg-promo-banner__text">${escapedText}</div>
                        <div class="cg-promo-banner__actions">
                            <div class="cg-promo-banner__not-applied" data-coupon-state="not-applied">
                                <button type="button" class="cg-promo-banner__apply-button" data-apply-coupon="${escapedTag}">
                                    <span>Apply coupon</span>
                                    <div class="loading__spinner hidden"></div>
                                </button>
                            </div>
                            <div class="cg-promo-banner__applied hidden" data-coupon-state="applied">
                                <div class="cg-promo-banner__applied-message">
                                    <i class="fa-solid fa-check-circle"></i>
                                    <span>Coupon applied</span>
                                </div>
                            </div>
                        </div>
                    </div>
                `;

                container.appendChild(item);
            });

            // Update states and show next coupon
            setTimeout(() => {
                this.updateCouponStates();
                this.showNextCoupon();
            }, 100);
        }

        // Update coupon states based on sessionStorage
        updateCouponStates() {
            if (!window.GlobalDiscountManager) {
                // Retry if GlobalDiscountManager is not loaded yet
                setTimeout(() => this.updateCouponStates(), 100);
                return;
            }

            this.elements.couponItems.forEach(item => {
                const discountTag = item.dataset.discountTag;
                if (!discountTag) return;

                const isApplied = window.GlobalDiscountManager.isDiscountApplied(discountTag);
                const notAppliedEl = item.querySelector(this.selectors.notApplied);
                const appliedEl = item.querySelector(this.selectors.applied);

                if (isApplied) {
                    if (notAppliedEl) notAppliedEl.classList.add(this.classes.hidden);
                    if (appliedEl) appliedEl.classList.remove(this.classes.hidden);
                } else {
                    if (notAppliedEl) notAppliedEl.classList.remove(this.classes.hidden);
                    if (appliedEl) appliedEl.classList.add(this.classes.hidden);
                }
            });
        }

        // Show the next unapplied coupon (hide all others)
        showNextCoupon() {
            if (!window.GlobalDiscountManager) {
                setTimeout(() => this.showNextCoupon(), 100);
                return;
            }

            // Find the first unapplied coupon
            const unappliedCoupon = Array.from(this.elements.couponItems).find(item => {
                const tag = item.dataset.discountTag;
                return tag && !window.GlobalDiscountManager.isDiscountApplied(tag);
            });

            // Hide all coupons
            this.elements.couponItems.forEach(item => {
                item.classList.add(this.classes.hidden);
            });

            // Show the next unapplied coupon
            if (unappliedCoupon) {
                unappliedCoupon.classList.remove(this.classes.hidden);
            } else {
                // All coupons applied - hide the entire section
                this.style.display = 'none';
            }
        }

        // Handle apply single coupon button click
        async handleApplySingleCoupon(couponTag, button) {
            if (!button || !couponTag) {
                console.error('CGCollectionPromoBanner: Invalid button or coupon tag');
                return;
            }

            const spinner = button.querySelector(this.selectors.spinner);
            const buttonText = button.querySelector(this.selectors.buttonText);
            
            if (!buttonText) {
                console.error('CGCollectionPromoBanner: Button text element not found');
                return;
            }
            
            // Show spinner and disable button
            if (spinner) spinner.classList.remove(this.classes.hidden);
            button.disabled = true;
            const originalText = buttonText.textContent;
            buttonText.textContent = 'Applying...';

            const shopUrl = window.shopUrl || window.location.origin;
            let couponApplied = false;
            let attempts = 0;
            const maxAttempts = 3;
            
            // Try to apply coupon with retry logic
            while (!couponApplied && attempts < maxAttempts) {
                try {
                    attempts++;
                    
                    // Apply discount via URL
                    const response = await fetch(`${shopUrl}/discount/${couponTag}`, {
                        method: 'GET',
                        credentials: 'include',
                        redirect: 'follow',
                        headers: {
                            'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8'
                        }
                    });
                    
                    if (response.status === 200 || response.status === 302) {
                        // Save to sessionStorage
                        if (window.GlobalDiscountManager) {
                            window.GlobalDiscountManager.saveAppliedDiscount(couponTag, couponTag, 'code');
                        }
                        
                        // Wait for Shopify to process
                        const delay = attempts === 1 ? 2000 : 1500;
                        await new Promise(resolve => setTimeout(resolve, delay));
                        
                        // Verify discount was actually applied to cart
                        if (window.GlobalDiscountManager) {
                            // Sync with cart first
                            await window.GlobalDiscountManager.syncWithCart();
                            
                            // Get fresh cart data
                            const cartResponse = await fetch(`${shopUrl}/cart.js`, {
                                method: 'GET',
                                credentials: 'include',
                                cache: 'no-store'
                            });
                            
                            if (cartResponse.ok) {
                                const cartData = await cartResponse.json();
                                const cartDiscounts = window.GlobalDiscountManager.getCartDiscountCodes(cartData);
                                
                                // Check if this discount is in the cart
                                couponApplied = cartDiscounts.some(code => {
                                    const codeLower = String(code || '').toLowerCase().trim();
                                    const tagLower = couponTag.toLowerCase().trim();
                                    return codeLower === tagLower || 
                                           codeLower.includes(tagLower) || 
                                           tagLower.includes(codeLower);
                                });
                                
                                if (couponApplied) {
                                    break; // Exit retry loop
                                } else if (attempts < maxAttempts) {
                                    // Wait longer before retry
                                    await new Promise(resolve => setTimeout(resolve, 1000));
                                }
                            }
                        } else {
                            // If GlobalDiscountManager not available, assume success
                            couponApplied = true;
                        }
                    } else {
                        console.warn(`Failed to apply coupon ${couponTag}: HTTP ${response.status}`);
                        if (attempts >= maxAttempts) {
                            break;
                        }
                    }
                } catch (error) {
                    console.error(`Error applying coupon ${couponTag} (attempt ${attempts}):`, error);
                    if (attempts >= maxAttempts) {
                        break;
                    } else {
                        // Wait before retry
                        await new Promise(resolve => setTimeout(resolve, 1000));
                    }
                }
            }

            // Update UI
            this.updateCouponStates();
            
            // Hide spinner
            if (spinner) spinner.classList.add(this.classes.hidden);
            
            if (couponApplied) {
                // Show applied state briefly, then show next coupon
                buttonText.textContent = 'Applied!';
                await new Promise(resolve => setTimeout(resolve, 500));
                
                // Show next coupon
                this.showNextCoupon();
            } else {
                // Failed to apply - re-enable button
                buttonText.textContent = originalText;
                button.disabled = false;
            }

            // Trigger cart update and price updates
            if (couponApplied) {
                if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof publish === 'function') {
                    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cg-collection-promo-banner' });
                }
                
                document.dispatchEvent(new CustomEvent('cg-ninja-dynamic-content:updated', {
                    bubbles: true
                }));
                
                // Dispatch sync event
                document.dispatchEvent(new CustomEvent('cg-ninja-discount:synced', {
                    bubbles: true
                }));
            }
        }
    }

    customElements.define('cg-collection-promo-banner', CGCollectionPromoBanner);
} else {
    console.warn('CGCollectionPromoBanner: Custom element already registered');
}